import { httpClient } from "../http-client";
import { extractApiData } from "./response-utils";

// ===== GDPR CONSENT API (ADR-008 §5 / W5-S2) =====
//
// Источник правды для FE enforcement-guard'ов AI-фич: чтение текущего статуса
// согласия через GET /gdpr/consent, фиксация — через POST /gdpr/consent.
// Контракт ответа — ADR-002: полезная нагрузка под response.data (extractApiData).

// Версия Terms of Service НЕ дублируется на FE: при записи согласия её
// проставляет backend из канонической CURRENT_TERMS_OF_SERVICE_VERSION
// (single source of truth, GDPR Art. 7). FE не присылает и не хранит версию —
// это исключает дрейф двух констант. Текущую версию (для отображения) при
// необходимости вернёт GET /gdpr/consent (termsVersion в снапшоте).

/** Типы согласий GDPR (зеркало backend GDPRConsentType). */
export enum GDPRConsentType {
  REFERRAL_PROGRAM = "REFERRAL_PROGRAM",
  MARKETING = "MARKETING",
  DATA_SHARING = "DATA_SHARING",
  AI_PROCESSING = "AI_PROCESSING",
}

/** Способ фиксации согласия (зеркало backend GDPRConsentMethod). */
export enum GDPRConsentMethod {
  CHECKBOX = "CHECKBOX",
  BUTTON = "BUTTON",
  API = "API",
}

/**
 * Нормализованный статус одного типа согласия (GET /gdpr/consent).
 * Зеркало backend `GDPRConsentStatusView`.
 */
export interface GDPRConsentStatusView {
  given: boolean;
  givenAt: string | null;
  withdrawnAt: string | null;
  termsVersion: string | null;
}

/**
 * Снапшот всех согласий пользователя. Ключи — camelCase поля jsonb
 * `user.gdprConsent`; типы без зафиксированного согласия отдаются как
 * `given:false` (fail-closed).
 */
export interface GDPRConsentSnapshot {
  referralProgram: GDPRConsentStatusView;
  marketing: GDPRConsentStatusView;
  dataSharing: GDPRConsentStatusView;
  aiProcessing: GDPRConsentStatusView;
}

export interface RecordConsentRequest {
  consentType: GDPRConsentType;
  given: boolean;
  consentMethod: GDPRConsentMethod;
  consentText: string;
  // termsVersion НЕ передаётся: backend проставляет каноническую версию
  // server-side (см. комментарий о single source of truth выше).
}

export const gdprApi = {
  /**
   * Текущий статус согласий пользователя (GDPR Art. 7 — прозрачность).
   * Backend: GET /gdpr/consent.
   */
  getConsents: async (): Promise<GDPRConsentSnapshot> => {
    const response = await httpClient.get<GDPRConsentSnapshot>("/gdpr/consent");
    return extractApiData<GDPRConsentSnapshot>(response);
  },

  /**
   * Зафиксировать согласие (GDPR Art. 7). Backend: POST /gdpr/consent.
   */
  recordConsent: async (
    payload: RecordConsentRequest,
  ): Promise<{ success: boolean }> => {
    const response = await httpClient.post<{ success: boolean }>(
      "/gdpr/consent",
      payload,
    );
    return extractApiData<{ success: boolean }>(response);
  },

  /**
   * Отозвать согласие (GDPR Art. 7(3)). Backend: DELETE /gdpr/consent.
   */
  withdrawConsent: async (
    consentType: GDPRConsentType,
  ): Promise<{ success: boolean }> => {
    const response = await httpClient.delete<{ success: boolean }>(
      "/gdpr/consent",
      { data: { consentType } },
    );
    return extractApiData<{ success: boolean }>(response);
  },
};

export default gdprApi;
